import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import Paper from "@mui/material/Paper";
import Draggable from "react-draggable";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Divider from "@mui/material/Divider";
import { TbViewportWide } from "react-icons/tb";
import Grid from "@mui/material/Grid";
import { AgGridReact } from "ag-grid-react";
import { statusBar } from "../../utils";
import { agGridTrans } from "../translation/i18n";
import { submitReportBatchesStart, resetReportBatchesState } from "../redux/project/projActions";
import toastr from "toastr";
import "./SelectReportType.scss";
import COLORS from "../styles/COLORS";

const DP_FLOW_REPORTS = ["CDS", "SPEC", "CALC", "FLOW"];
const FLOW_REPORTS = ["CDS", "FCS"]; // ROLL_UP is CDS and FCS combined
const DP_LEVEL_REPORTS = ["CDS", "SPEC", "PERF"];
const DEFAULT_REPORTS = ["CDS", "SPEC"];

const getReportSelectionKey = (category, value) => `${category}::${value}`;

const getReportsByTechnologyType = (technologyType) => {
  const normalizedTechnologyType = technologyType?.trim()?.toUpperCase();

  if (normalizedTechnologyType === "DP FLOW") {
    return DP_FLOW_REPORTS.map((report) => ({ value: report, label: report }));
  }

  if (normalizedTechnologyType === "FLOW") {
    return FLOW_REPORTS.map((report) => ({ value: report, label: report }));
  }

  if (normalizedTechnologyType === "DP LEVEL") {
    return DP_LEVEL_REPORTS.map((report) => ({ value: report, label: report }));
  }

  return DEFAULT_REPORTS.map((report) => ({ value: report, label: report }));
};

const getReportBatchErrorMessage = (errorPayload, fallbackMessage) => {
  if (!errorPayload) {
    return fallbackMessage;
  }

  if (typeof errorPayload === "string") {
    return errorPayload;
  }

  if (Array.isArray(errorPayload)) {
    return getReportBatchErrorMessage(errorPayload[0], fallbackMessage);
  }

  if (typeof errorPayload === "object") {
    return (
      getReportBatchErrorMessage(errorPayload.message, "") ||
      getReportBatchErrorMessage(errorPayload.detail, "") ||
      getReportBatchErrorMessage(errorPayload.failure_summary, "") ||
      getReportBatchErrorMessage(errorPayload.error, "") ||
      getReportBatchErrorMessage(errorPayload.title, "") ||
      getReportBatchErrorMessage(errorPayload.errors, fallbackMessage) ||
      getReportBatchErrorMessage(errorPayload.non_field_errors, fallbackMessage) ||
      Object.values(errorPayload)
        .map((value) => getReportBatchErrorMessage(value, ""))
        .find(Boolean) ||
      fallbackMessage
    );
  }

  return fallbackMessage;
};

function PaperComponent(props) {
  return (
    <Draggable handle="#draggable-dialog-title">
      <Paper {...props} />
    </Draggable>
  );
}

const SelectReportType = ({ open, onClose, selectedRowsData, revision }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const ctrl = useSelector((state) => state.ctrl);
  const reportBatchesPhase = useSelector((state) => state.project.reportBatchesPhase);
  const reportBatchesError = useSelector((state) => state.project.reportBatchesError);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [groupBy, setGroupBy] = useState("tag_number");
  const [reportFormat, setReportFormat] = useState("pdf");
  const [techTypeCategories, setTechTypeCategories] = useState([]);
  const [reportInputExpand, setReportInputExpand] = useState(false);
  const [rowDataRI, setRowDataRI] = useState([]);
  // const [gridApi, setGridApi] = useState(null);

  const handleCheckboxChange = (category, value) => {
    const selectionKey = getReportSelectionKey(category, value);

    setSelectedTypes((prev) =>
      prev.includes(selectionKey)
        ? prev.filter((selectedValue) => selectedValue !== selectionKey)
        : [...prev, selectionKey],
    );
  };

  const handleSelectAllReportsChange = (event) => {
    if (event.target.checked) {
      const allSelectionKeys = techTypeCategories.flatMap((category) =>
        category.reports.map((report) => getReportSelectionKey(category.category, report.value)),
      );

      setSelectedTypes(allSelectionKeys);
      return;
    }

    setSelectedTypes([]);
  };

  const eligibleSelectedRows = (selectedRowsData || []).filter((row) => row?.pur_pa_id || row?.pur_configurator_id);
  const totalSelectedTags = eligibleSelectedRows.length;

  const handleSelect = () => {
    if (selectedTypes.length > 0) {
      const groupByMapping = {
        tag_number: "tag",
        report_type: "report",
      };

      const selectedReports = techTypeCategories.flatMap((category) =>
        category.reports
          .filter((report) => selectedTypes.includes(getReportSelectionKey(category.category, report.value)))
          .map((report) => ({
            category: category.category,
            value: report.value,
            label: report.label,
          })),
      );

      const selectedReportsByProduct = selectedReports.reduce((reportConfig, report) => {
        if (!reportConfig[report.category]) {
          reportConfig[report.category] = [];
        }

        if (!reportConfig[report.category].includes(report.value)) {
          reportConfig[report.category].push(report.value);
        }

        return reportConfig;
      }, {});

      const reportConfig = Object.values(
        eligibleSelectedRows.reduce((configByProduct, row) => {
          const technologyType = row.gen_technology_type;
          const productDesc = row.gen_prod_desc;
          const reportTypes = selectedReportsByProduct[productDesc];

          if (!productDesc || !reportTypes || reportTypes.length === 0) {
            return configByProduct;
          }

          if (!configByProduct[productDesc]) {
            configByProduct[productDesc] = {
              tag_ids: [],
              report_types: reportTypes,
              gen_technology_type: technologyType,
              gen_prod_desc: productDesc,
            };
          }

          if (row.tag_id !== undefined && row.tag_id !== null) {
            configByProduct[productDesc].tag_ids.push(row.tag_id);
          }

          return configByProduct;
        }, {}),
      );

      dispatch(
        submitReportBatchesStart({
          package_id: ctrl?.pkg?.package_id,
          revision: Number(revision?.value || ctrl?.pkg?.active_revision),
          group_by: groupByMapping[groupBy] || groupBy,
          report_config: reportConfig,
        }),
      );

      console.log("Report Batch Submission Payload:", {
        package_id: ctrl?.pkg?.package_id,
        revision: Number(revision?.value || ctrl?.pkg?.active_revision),
        group_by: groupByMapping[groupBy] || groupBy,
        report_config: reportConfig,
      });

      setSelectedTypes([]);
      setGroupBy("tag_number");
      setReportFormat("pdf");
      onClose();
    }
  };

  const openReportInput = (category) => {
    setReportInputExpand(true);
    let data = eligibleSelectedRows
      .filter((row) => row.gen_prod_desc === category)
      .map((row) => {
        return {
          tag_number: row.tag_number,
          pur_pa_id: row.pur_pa_id,
          config_status: row.config_status,
        };
      });
    setRowDataRI(data);
  };

  const closeReportInput = () => {
    setReportInputExpand(false);
    setRowDataRI([]);
  };

  const onClickCell = (e) => {
    if (e.colDef.field === "reportinput") {
      setRowDataRI(e.data.reportinput);
    }
  };

  const [colDefs2] = useState([
    {
      headerName: "",
      minWidth: 70,
      maxWidth: 60,
      valueFormatter: (params) => {
        return `${parseInt(params.node.id) + 1}`;
      },
    },
    { headerName: t("Tag Number"), field: "tag_number", filter: "agTextColumnFilter", minWidth: 220, resizable: true },
    { headerName: t("PA Ref ID"), field: "pur_pa_id", filter: "agTextColumnFilter", minWidth: 220, resizable: true },
    {
      headerName: t("Configuration Status"),
      field: "config_status",
      filter: "agTextColumnFilter",
      minWidth: 230,
      resizable: true,
    },
  ]);

  const defaultColDef = {
    sortable: true,
    resizable: true,
    filter: true,
    editable: false,
    sortingOrder: ["asc", "desc", " "],
    filterParams: {
      buttons: ["apply", "cancel"],
      closeOnApply: true,
    },
    menuTabs: ["filterMenuTab", "generalMenuTab", "columnsMenuTab"],
  };

  useEffect(() => {
    const productDescriptions = eligibleSelectedRows.length
      ? [...new Set(eligibleSelectedRows.map((row) => row.gen_prod_desc).filter(Boolean))]
      : [];

    if (productDescriptions.length === 0) {
      setTechTypeCategories([]);
      return;
    }

    const selectedTagsByProduct = eligibleSelectedRows.reduce((counts, row) => {
      const productDesc = row?.gen_prod_desc;

      if (!productDesc) {
        return counts;
      }

      counts[productDesc] = (counts[productDesc] || 0) + 1;
      return counts;
    }, {});

    const categories = productDescriptions.map((desc) => {
      const firstMatchingRow = eligibleSelectedRows.find((row) => row.gen_prod_desc === desc);
      const technologyType = firstMatchingRow?.gen_technology_type;

      return {
        category: desc,
        technologyType: technologyType,
        reports: getReportsByTechnologyType(technologyType),
        selectedTags: selectedTagsByProduct[desc] || 0,
      };
    });

    setTechTypeCategories(categories);
  }, [selectedRowsData]);

  useEffect(() => {
    if (!open || techTypeCategories.length === 0) {
      return;
    }

    setSelectedTypes((prevSelectedTypes) => {
      if (prevSelectedTypes.length > 0) {
        return prevSelectedTypes;
      }

      return techTypeCategories.flatMap((category) =>
        category.reports.map((report) => getReportSelectionKey(category.category, report.value)),
      );
    });
  }, [open, techTypeCategories]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (totalSelectedTags === 0) {
      toastr.error(t("No sized or configured tags selected for batch report."));
      onClose();
      return;
    }

    if (totalSelectedTags > 200) {
      toastr.error(t("You can select up to 200 tags for batch report."));
      onClose();
    }
  }, [open, onClose, t, totalSelectedTags]);

  useEffect(() => {
    if (reportBatchesPhase === "SUCCESS") {
      toastr.success(t("Report batch submitted successfully."));
      dispatch(resetReportBatchesState());
    }

    if (reportBatchesPhase === "ERROR") {
      const errorMessage = getReportBatchErrorMessage(reportBatchesError, t("Failed to submit report batch."));
      toastr.error(errorMessage);
      dispatch(resetReportBatchesState());
    }
  }, [dispatch, reportBatchesError, reportBatchesPhase, t]);

  const handleClose = () => {
    setSelectedTypes([]);
    setGroupBy("tag_number");
    setReportFormat("pdf");
    onClose();
  };

  const totalAvailableReports = techTypeCategories.reduce((total, category) => total + category.reports.length, 0);

  const allReportsSelected = totalAvailableReports > 0 && selectedTypes.length === totalAvailableReports;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
        className="create-project tags-modal muiForm-Modal"
        PaperComponent={PaperComponent}
        aria-labelledby="draggable-dialog-title"
      >
        <DialogTitle className="model-title" id="draggable-dialog-title">
          <div className="d-flex">
            <div>{t("Batch Report")}</div>
          </div>
        </DialogTitle>
        <DialogContent className="selectReportType-content">
          <div className="select-report-layout pb-3 pt-2">
            <section className="report-section">
              <div className="section-header">
                <div>
                  <h3>Select the type of report(s) that you want to generate for each Product Description.</h3>
                  <Typography variant="body2" className="selected-tags-summary">
                    {t("Total Selected Tags")}: {totalSelectedTags}
                  </Typography>
                  <Typography variant="caption" className="report-availability-note">
                    {t(
                      "Only Tags that are already Sized or Configured are shown here. Depending on a Tag's Sizing/Configuration, only specific reports may be generated.",
                    )}
                  </Typography>
                </div>
                <FormControlLabel
                  className="select-all-toggle"
                  control={
                    <Checkbox size="small" checked={allReportsSelected} onChange={handleSelectAllReportsChange} />
                  }
                  label={t("All Available Reports")}
                />
              </div>

              <div className="report-categories-grid">
                {techTypeCategories
                  ? techTypeCategories.map((cat) => (
                    <div className="report-category-card" key={cat.category}>
                      <div className="category-header">
                        <Typography variant="subtitle1" className="category-title">
                          {t(cat.category)}
                          <span style={{ fontSize: "13px", color: COLORS.disabledGrey, marginLeft: "5px" }}>
                            {cat.technologyType}
                          </span>
                        </Typography>
                        <button
                          type="button"
                          className="report-input-trigger"
                          onClick={() => openReportInput(cat.category)}
                        >
                          <TbViewportWide className="expandReport" />
                          <span>{t("View Tags")}</span>
                          <span className="tag-counts">{` (${cat.selectedTags})`}</span>
                        </button>
                      </div>

                      <FormControl component="fieldset" fullWidth>
                        <FormGroup className="report-checkboxes">
                          {cat.reports.map((report) => (
                            <FormControlLabel
                              key={report.value}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={selectedTypes.includes(getReportSelectionKey(cat.category, report.value))}
                                  onChange={() => handleCheckboxChange(cat.category, report.value)}
                                />
                              }
                              label={t(report.label)}
                            />
                          ))}
                        </FormGroup>
                      </FormControl>
                    </div>
                  ))
                  : null}
              </div>
            </section>

            <Divider />

            <section className="report-section report-options-section">
              <h3>Choose how reports should be grouped</h3>
              <div className="report-grouping-grid">
                <FormControl component="fieldset" className="report-option-card">
                  <FormLabel component="legend" className="category-title">
                    {t("Group by")}
                  </FormLabel>
                  <RadioGroup row className="radio-grid" value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
                    <FormControlLabel value="tag_number" control={<Radio />} label={t("Tag Number")} />
                    <FormControlLabel value="report_type" control={<Radio />} label={t("Report Type")} />
                  </RadioGroup>
                </FormControl>
                {/* <FormControl component="fieldset" className="report-option-card">
                <FormLabel component="legend" className="category-title">{t('Choose the reports format')}</FormLabel>
                <RadioGroup
                  row
                  className="radio-grid"
                  value={reportFormat}
                  onChange={(e) => setReportFormat(e.target.value)}
                >
                  <FormControlLabel value="pdf" control={<Radio />} label={t('PDF')} />
                  <FormControlLabel value="xls" control={<Radio />} label={t('XLS')} />
                </RadioGroup>
              </FormControl> */}
              </div>
            </section>
          </div>
        </DialogContent>
        <DialogActions className="model-actions" style={{ paddingRight: "16px" }}>
          <Button className="cnbtn btn btn-outline-success" variant="outlined" onClick={handleClose}>
            {t("Cancel")}
          </Button>
          <Button
            className="sbtn btn btn-secondary"
            variant="contained"
            color="success"
            disabled={selectedTypes.length === 0 || totalSelectedTags === 0}
            onClick={handleSelect}
          >
            {t("Generate Reports")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        maxWidth="sm"
        open={reportInputExpand}
        onClose={closeReportInput}
        className="viewReportInput-model muiForm-Modal"
        PaperComponent={PaperComponent}
        aria-labelledby="draggable-dialog-title"
      >
        <DialogTitle className="model-title mb-0 text-align-center">
          <div className="header-row">
            <div id="draggable-dialog-title" className="">
              {t("Report Input")}
            </div>
          </div>
        </DialogTitle>
        <DialogContent>
          <Grid container className="ag-theme-alpine marginRow muiForm mb-2 pt-3" spacing={1.4}>
            <Grid item md={12} xs={12}>
              <div style={{ height: "60vh" }}>
                <AgGridReact
                  statusBar={statusBar}
                  defaultColDef={defaultColDef}
                  rowData={rowDataRI}
                  localeText={agGridTrans[localStorage.getItem("lang")]}
                  // onGridReady={onGridReady}
                  columnDefs={colDefs2}
                  headerHeight={30}
                  rowHeight={26}
                  onCellClicked={(e) => onClickCell(e)}
                ></AgGridReact>
              </div>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions className="model-actions" style={{ paddingRight: "24px" }}>
          <button className="cnbtn btn cancel-btn" variant="outlined" onClick={closeReportInput}>
            {t("Close")}
          </button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SelectReportType;
